import { useCallback, useEffect, useRef, useState } from "react";
import { isAxiosError } from "axios";
import {
  getJobStatus,
  requestFullReport,
} from "@/features/jobs/api/jobStatusApi";
import { ReportJobStatus } from "@/shared/interfaces/JobStatus";
import { useJobStore } from "@/store/jobStore";

const POLL_INTERVAL_MS = 3_000;
const POLL_TIMEOUT_MS = 10 * 60_000;

// The job is still working through these (and raw pipeline statuses like
// STARTED/GENERATING/EXTRACTING). Anything else is terminal.
const isJobRunning = (status: string): boolean =>
  status !== ReportJobStatus.PENDING_REVIEW &&
  status !== ReportJobStatus.AWAITING_INPUT &&
  status !== ReportJobStatus.FAILED;

interface UseGenerateFullReportArgs {
  /** Report id — also the jobId for the full-report job. Null while loading. */
  reportId: string | null;
  /** Evidence ids backing the report — recorded on the tracked job entry. */
  evidenceIDs?: string[];
  /** Video titles backing the report — shown as the job's label in the tracker. */
  videoTitles?: string[];
  /**
   * Current narrative-only mode of the report. The run only "finishes" — and
   * the report unlocks back to the full edit view — once this flips to false
   * after onComplete refetches. The job can report PENDING_REVIEW a tick
   * before the metadata flag updates, so we hold the lock until the flip
   * lands rather than briefly re-showing the stale narrative-only view.
   */
  narrativeOnly: boolean;
  /** Called once the job finishes successfully — refetches report data. */
  onComplete: () => void | Promise<void>;
  showError: (message: string) => void;
  showSuccess: (message: string) => void;
}

/**
 * Triggers full-report generation for a narrative-only report (POST /jobs with
 * { fullReport: true, jobId: reportId }) and tracks the job to completion by
 * polling GET /jobStatus. While the job runs `isGeneratingFull` is true; on
 * success it invokes `onComplete` (the editor refetches and flips from
 * narrative-only to the full schema form).
 */
export const useGenerateFullReport = ({
  reportId,
  evidenceIDs,
  videoTitles,
  narrativeOnly,
  onComplete,
  showError,
  showSuccess,
}: UseGenerateFullReportArgs) => {
  const [isGeneratingFull, setIsGeneratingFull] = useState(false);
  // Latest narrative-only flag, read inside the poll loop without re-creating
  // the interval. Lets us hold the lock until the editor flips to full mode.
  const narrativeOnlyRef = useRef(narrativeOnly);
  narrativeOnlyRef.current = narrativeOnly;
  // Wall-clock start of the in-flight run, driving the progress countdown the
  // editor shows while generation blocks the report. Null when idle.
  const [generationStartedAt, setGenerationStartedAt] = useState<number | null>(
    null,
  );
  const addJob = useJobStore((state) => state.addJob);
  const removeJob = useJobStore((state) => state.removeJob);
  const intervalRef = useRef<number | null>(null);
  const deadlineRef = useRef<number>(0);

  const clearPolling = useCallback(() => {
    if (intervalRef.current !== null) {
      window.clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  // Single teardown for every terminal path (success, failure, timeout):
  // stop polling and drop the in-flight + countdown state together.
  const endGeneration = useCallback(() => {
    clearPolling();
    setIsGeneratingFull(false);
    setGenerationStartedAt(null);
  }, [clearPolling]);

  // Stop polling if the page unmounts mid-run.
  useEffect(() => clearPolling, [clearPolling]);

  const startGeneration = useCallback(async () => {
    if (!reportId || isGeneratingFull) return;
    setIsGeneratingFull(true);
    try {
      await requestFullReport(reportId);
      // Surface the run in the Report Tracker. The full-report job reuses the
      // reportId as its jobId, so clear any stale entry (e.g. the original
      // narrative job left at PENDING_REVIEW) before registering the fresh,
      // active one — the status-sync fan-out then polls it automatically.
      removeJob(reportId);
      addJob({
        id: reportId,
        reportId,
        status: ReportJobStatus.PENDING,
        progress: 0,
        evidenceIDs: evidenceIDs ?? [],
        videoTitles,
      });
      showSuccess(
        "Generating the full report — this runs in the background and may take a few minutes.",
      );
    } catch (err) {
      setIsGeneratingFull(false);
      showError(
        (isAxiosError(err) && err.response?.data?.error) ||
          "Failed to start full report generation.",
      );
      return;
    }

    const now = Date.now();
    setGenerationStartedAt(now);
    deadlineRef.current = now + POLL_TIMEOUT_MS;
    clearPolling();
    intervalRef.current = window.setInterval(async () => {
      if (Date.now() >= deadlineRef.current) {
        endGeneration();
        showError(
          "Full report generation is taking longer than expected. Refresh to check its status.",
        );
        return;
      }
      try {
        const { jobStatus } = await getJobStatus(reportId);
        if (isJobRunning(jobStatus)) return;
        if (jobStatus === ReportJobStatus.FAILED) {
          endGeneration();
          showError("Full report generation failed. Please try again.");
          return;
        }
        // PENDING_REVIEW (or AWAITING_INPUT): the run produced a result —
        // refetch so the editor reflects the now-full report.
        await onComplete();
        // Hold the lock until the report has actually flipped to full mode.
        // getJobStatus can report terminal a tick before metadata.narrativeOnly
        // updates; unlocking now would flash the stale narrative-only view
        // instead of the original edit view. Keep polling (and refetching) on
        // the next tick until the flip lands.
        if (narrativeOnlyRef.current) return;
        endGeneration();
      } catch {
        // Transient status-check failure: keep polling until the deadline.
      }
    }, POLL_INTERVAL_MS);
  }, [
    reportId,
    isGeneratingFull,
    clearPolling,
    endGeneration,
    onComplete,
    showError,
    showSuccess,
    addJob,
    removeJob,
    evidenceIDs,
    videoTitles,
  ]);

  return {
    isGeneratingFull,
    startGeneration,
    generationStartedAt,
    generationTimeoutMs: POLL_TIMEOUT_MS,
  };
};
